import { resolveAppEnvironment, type AppEnvironmentKey } from '../appEnvironment';
import {
  APP_BUILD_ISO_TIME,
  APP_BUILD_VERSION,
  APP_PLAY_VERSION_CODE,
  APP_PLAY_VERSION_NAME,
} from '../generated/buildInfo';

export type AppBuildInfo = {
  environmentKey: AppEnvironmentKey;
  /** Short env label: localhost / dev / app */
  environmentLabel: string;
  version: string;
  /** Google Play versionName (`1.0.N`), from android-play-version.json */
  playVersionName: string;
  /** Google Play versionCode (N) */
  playVersionCode: number;
  updatedAtIso: string | null;
  updatedAtDisplay: string | null;
};

/** User-facing environment name for the build-info popover. */
export function appEnvironmentShortLabel(key: AppEnvironmentKey): string {
  switch (key) {
    case 'local':
      return 'localhost';
    case 'dev':
      return 'dev';
    case 'app':
      return 'app';
    default:
      return 'app';
  }
}

export function formatAppBuildUpdatedAt(iso: string | null | undefined, locale: string): string | null {
  const trimmed = iso?.trim();
  if (!trimmed) {
    return null;
  }
  const date = new Date(trimmed);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  const tag = locale === 'he' ? 'he-IL' : 'en-GB';
  try {
    return new Intl.DateTimeFormat(tag, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(date);
  } catch {
    return trimmed;
  }
}

export function resolveAppBuildInfo(locale: string): AppBuildInfo {
  const env = resolveAppEnvironment();
  const updatedAtIso = APP_BUILD_ISO_TIME.trim() || null;
  const playCode =
    Number.isInteger(APP_PLAY_VERSION_CODE) && APP_PLAY_VERSION_CODE >= 1
      ? APP_PLAY_VERSION_CODE
      : 1;
  const playName = APP_PLAY_VERSION_NAME.trim() || `1.0.${playCode}`;
  return {
    environmentKey: env.key,
    environmentLabel: appEnvironmentShortLabel(env.key),
    version: APP_BUILD_VERSION.trim() || '0.0.0',
    playVersionName: playName,
    playVersionCode: playCode,
    updatedAtIso,
    updatedAtDisplay: formatAppBuildUpdatedAt(updatedAtIso, locale),
  };
}

/** Subtle one-line stamp for support (version + last deploy time). */
export function formatAppBuildCompactStamp(locale: string): string {
  const info = resolveAppBuildInfo(locale);
  const when = info.updatedAtDisplay ?? '?';
  return `v${info.version} · ${when}`;
}
